import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../../api/apiClient";
import { useConfirm } from "../../context/confirmContext";
import useSettingsPermissions from "../../Pages/Settings/hooks/useSettingsPermissions";
import ColorDot from "../ColorDot/ColorDot";
import { generateMenuPdf } from "../../utils/menu/generateMenuPdf";
import { BRAND_STYLES, buildMenuModel, demsLabel } from "../../utils/menu/menuModel";
import { POTENCY_TIERS, aromaForColor, demsTier, menuColor, potencyTier } from "../../utils/menu/menuMetrics";
import { byUploadedDesc } from "../../utils/sortByUploaded";
import "./generateMenuModal.css";

const BRAND_STYLE_KEY = "menu_brand_style";
const PRICING_SETTINGS_PATH = "/settings/company/pricing";
const COMPANY_PROFILE_PATH = "/settings/company/profile";
// Select value for the "add a tier" entry; never stored as a product's tier.
const ADD_TIER_OPTION = "__add_tier__";

const BRAND_STYLE_OPTIONS = [
    { value: BRAND_STYLES.LOGO, label: "Logo", needsLogo: true },
    { value: BRAND_STYLES.NAME, label: "Name", needsLogo: false },
    { value: BRAND_STYLES.BOTH, label: "Logo and name", needsLogo: true },
];

// Read a remembered choice, falling back when nothing valid is stored.
function loadChoice(key, allowed, fallback) {
    try {
        const saved = localStorage.getItem(key);
        return Object.values(allowed).includes(saved) ? saved : fallback;
    } catch {
        return fallback;
    }
}

function saveChoice(key, value) {
    try {
        localStorage.setItem(key, value);
    } catch {
        // Storage unavailable — the choice simply is not remembered.
    }
}

function todayIso() {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

const isOnShelf = (value) => value !== 0 && value != null;

const GenerateMenuModal = ({ licenseNo, licenseName, locationId, locationName, onClose }) => {
    const [loading, setLoading] = useState(true);
    const [products, setProducts] = useState([]);
    const [tiers, setTiers] = useState([]);
    const [logoUrl, setLogoUrl] = useState("");
    // True once the license lookup has answered, so "Add logo" is not offered while it is still loading
    const [logoChecked, setLogoChecked] = useState(false);
    const [edits, setEdits] = useState({});
    // Preferred header style; only honoured while the license has a logo (see brandStyle below)
    const [preferredBrandStyle, setPreferredBrandStyle] = useState(() => loadChoice(BRAND_STYLE_KEY, BRAND_STYLES, BRAND_STYLES.LOGO));
    const [menuDate, setMenuDate] = useState(todayIso);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const confirm = useConfirm();
    // Same rule that guards the Settings > Pricing route
    const { canSeePricing, canSeeCompanyProfile } = useSettingsPermissions();

    const hasLogo = !!logoUrl;
    const brandStyle = hasLogo ? preferredBrandStyle : BRAND_STYLES.NAME;

    useEffect(() => {
        const locationParam = locationId ? `&locationId=${locationId}` : "";
        Promise.all([
            apiClient.get(`/products/menu?licenseNo=${licenseNo}${locationParam}`),
            apiClient.get(`/pricing-tiers?licenseNo=${licenseNo}`),
        ])
            .then(([productsRes, tiersRes]) => {
                // Listed like the Inventory table and the weigh-in list: newest upload first.
                // This is the order of this window only; the PDF orders cards by color, then name.
                const loaded = [...(productsRes.data || [])].sort(byUploadedDesc);
                setProducts(loaded);
                setTiers(tiersRes.data || []);
                const initialEdits = {};
                loaded.forEach((p) => {
                    initialEdits[p.product_id] = {
                        price_tier_id: p.price_tier_id ?? "",
                        on_shelf: isOnShelf(p.on_shelf),
                    };
                });
                setEdits(initialEdits);
            })
            .catch(() => setError("Failed to load products. Please try again."))
            .finally(() => setLoading(false));

        // The logo is optional on the menu; a failure here just means it prints without one.
        apiClient
            .get(`/license/get-by-license/${licenseNo}`)
            .then((res) => {
                setLogoUrl(res.data?.[0]?.logo || "");
                setLogoChecked(true);
            })
            .catch(() => setLogoUrl(""));
    }, [licenseNo, locationId]);

    const setField = (productId, key, value) =>
        setEdits((prev) => ({
            ...prev,
            [productId]: { ...prev[productId], [key]: value },
        }));

    const handleBrandStyle = (style) => {
        setPreferredBrandStyle(style);
        saveChoice(BRAND_STYLE_KEY, style);
    };

    const onShelfProducts = useMemo(
        () =>
            products
                .filter((p) => (edits[p.product_id] || {}).on_shelf)
                .map((p) => ({ ...p, price_tier_id: edits[p.product_id].price_tier_id || null })),
        [products, edits]
    );

    const model = useMemo(
        () =>
            buildMenuModel({
                products: onShelfProducts,
                tiers,
                date: menuDate,
                companyName: licenseName,
                locationName,
                brandStyle,
            }),
        [onShelfProducts, tiers, menuDate, licenseName, locationName, brandStyle]
    );

    // Products whose shelf status or tier differs from what was loaded
    const dirtyProducts = products.filter((p) => {
        const edit = edits[p.product_id] || {};
        return String(edit.price_tier_id ?? "") !== String(p.price_tier_id ?? "") ||
               edit.on_shelf !== isOnShelf(p.on_shelf);
    });

    // Leave for a Settings page, warning first if shelf or tier edits would be lost.
    const leaveForSettings = async (path, destination, purpose) => {
        if (dirtyProducts.length > 0) {
            const leave = await confirm({
                title: "Leave Menu Preview",
                message: `Go to Settings > ${destination} to ${purpose}? Shelf and price tier changes made here have not been saved and will be lost.`,
                confirmLabel: `Go to ${destination}`,
                cancelLabel: "Stay",
                variant: "warning",
            });
            if (!leave) return;
        }
        onClose();
        navigate(path);
    };

    const handleTierChange = (productId, value) => {
        if (value === ADD_TIER_OPTION) {
            leaveForSettings(PRICING_SETTINGS_PATH, "Pricing", "add a tier");
            return;
        }
        setField(productId, "price_tier_id", value);
    };

    const blockers = menuDate ? model.blockers : [{ key: "no-date", message: "Choose a menu date." }, ...model.blockers];
    const onShelfCount = onShelfProducts.length;

    const handleGenerate = async () => {
        setSaving(true);
        setError(null);
        try {
            await Promise.all(dirtyProducts.map((p) => {
                const edit = edits[p.product_id] || {};
                return apiClient.patch(`/products/${p.product_id}/inventory`, {
                    price_tier_id: edit.price_tier_id || null,
                    on_shelf: edit.on_shelf ? 1 : 0,
                });
            }));

            const filename = `${(licenseName || "menu").replace(/[^\w-]+/g, "_")}_flower_menu_${menuDate}.pdf`;
            await generateMenuPdf(model, filename, { licenseNo });
            onClose();
        } catch {
            setError("Failed to generate menu. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="gmm-overlay">
                <div className="gmm-modal">
                    <div className="gmm-header">
                        <h3 className="gmm-title">Generate Menu</h3>
                    </div>
                    <p className="gmm-loading">Loading products...</p>
                </div>
            </div>
        );
    }

    if (error && products.length === 0) {
        return (
            <div className="gmm-overlay">
                <div className="gmm-modal">
                    <div className="gmm-header">
                        <h3 className="gmm-title">Generate Menu</h3>
                    </div>
                    <p className="gmm-error">{error}</p>
                    <div className="gmm-footer">
                        <button className="gmm-btn gmm-btn--cancel" onClick={onClose}>Close</button>
                    </div>
                </div>
            </div>
        );
    }

    if (products.length === 0) {
        return (
            <div className="gmm-overlay">
                <div className="gmm-modal">
                    <div className="gmm-header">
                        <h3 className="gmm-title">Generate Menu</h3>
                    </div>
                    <p className="gmm-empty">No flower products with ≥1 gram in stock were found.</p>
                    <div className="gmm-footer">
                        <button className="gmm-btn gmm-btn--cancel" onClick={onClose}>Close</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="gmm-overlay">
            <div className="gmm-modal">
                <div className="gmm-header">
                    <div>
                        <h3 className="gmm-title">Menu Preview</h3>
                        <p className="gmm-subtitle">
                            {locationName || (locationId ? `Location ${locationId}` : "Master inventory")} — {onShelfCount} of {products.length} product{products.length !== 1 ? "s" : ""} on shelf.
                            Toggle shelf status and assign price tiers, then generate the PDF.
                        </p>
                    </div>
                </div>

                <div className="gmm-options">
                    <fieldset className="gmm-option">
                        <legend className="gmm-option-label">Menu header</legend>
                        <div className="gmm-option-row">
                            <div className="gmm-segmented">
                                {BRAND_STYLE_OPTIONS.filter((option) => hasLogo || !option.needsLogo).map((option) => (
                                    <label
                                        key={option.value}
                                        className={`gmm-segment${brandStyle === option.value ? " gmm-segment--active" : ""}`}
                                    >
                                        <input
                                            type="radio"
                                            name="gmm-brand-style"
                                            className="gmm-segment-input"
                                            value={option.value}
                                            checked={brandStyle === option.value}
                                            onChange={() => handleBrandStyle(option.value)}
                                        />
                                        {option.label}
                                    </label>
                                ))}
                            </div>
                            {!hasLogo && logoChecked && canSeeCompanyProfile && (
                                <button
                                    type="button"
                                    className="gmm-link-btn"
                                    onClick={() => leaveForSettings(COMPANY_PROFILE_PATH, "Company Profile", "add a logo")}
                                >
                                    + Add logo
                                </button>
                            )}
                        </div>
                    </fieldset>
                    <label className="gmm-option">
                        <span className="gmm-option-label">Menu date</span>
                        <input
                            type="date"
                            className="gmm-input gmm-input--date"
                            value={menuDate}
                            onChange={(e) => setMenuDate(e.target.value)}
                        />
                    </label>
                </div>

                {error && <p className="gmm-error">{error}</p>}

                <div className="gmm-body">
                    <table className="gmm-table">
                        <thead>
                            <tr>
                                <th className="gmm-th--shelf">On Shelf</th>
                                <th>Product</th>
                                <th className="gmm-th--qty">Qty (g)</th>
                                <th>Price Tier</th>
                                <th>Aroma</th>
                                <th>Potency</th>
                                <th>DEMS</th>
                            </tr>
                        </thead>
                        <tbody>
                            {products.map((p) => {
                                const edit = edits[p.product_id] || {};
                                const onShelf = !!edit.on_shelf;
                                const missingTier = onShelf && (edit.price_tier_id === "" || edit.price_tier_id == null);
                                const qty = parseFloat(p.quantity);
                                const qtyDisplay = isNaN(qty) ? "—" : `${qty % 1 === 0 ? qty : qty.toFixed(2)}g`;
                                const color = menuColor(p);
                                const potency = potencyTier(p);
                                const dems = demsTier(p);
                                const missingClass = (value) => (onShelf && !value ? "gmm-td--missing" : "");
                                return (
                                    <tr key={p.product_id} className={!onShelf ? "gmm-row--off-shelf" : ""}>
                                        <td className="gmm-td--shelf">
                                            <input
                                                type="checkbox"
                                                className="gmm-shelf-checkbox"
                                                aria-label={`${p.product_name} on shelf`}
                                                checked={onShelf}
                                                onChange={(e) => setField(p.product_id, "on_shelf", e.target.checked)}
                                            />
                                        </td>
                                        <td className="gmm-product-name">
                                            {color && <ColorDot color={color} className="gmm-dot" />}
                                            {p.product_name}
                                        </td>
                                        <td className="gmm-td--qty">{qtyDisplay}</td>
                                        <td>
                                            <select
                                                className={`gmm-select${missingTier ? " gmm-input--missing" : ""}`}
                                                aria-label={`${p.product_name} price tier`}
                                                value={edit.price_tier_id || ""}
                                                disabled={!onShelf}
                                                onChange={(e) => handleTierChange(p.product_id, e.target.value)}
                                            >
                                                <option value="">{onShelf ? "Select a tier..." : "—"}</option>
                                                {tiers.map((t) => (
                                                    <option key={t.id} value={t.id}>{t.name}</option>
                                                ))}
                                                {canSeePricing && (
                                                    <option value={ADD_TIER_OPTION}>+ Add pricing tier…</option>
                                                )}
                                            </select>
                                        </td>
                                        <td>{aromaForColor(color) || "—"}</td>
                                        <td className={missingClass(potency)}>{potency ? POTENCY_TIERS[potency].label : "No data"}</td>
                                        <td className={missingClass(dems)}>{dems ? demsLabel(dems) : "No data"}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {onShelfCount > 0 && blockers.length > 0 && (
                    <div className="gmm-blockers" role="alert">
                        <p className="gmm-blockers-title">Fix before generating:</p>
                        <ul className="gmm-blockers-list">
                            {blockers.map((b) => (
                                <li key={b.key}>{b.message}</li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className="gmm-footer">
                    {onShelfCount === 0 && (
                        <span className="gmm-footer-hint">Tick at least one product as on shelf.</span>
                    )}
                    <button className="gmm-btn gmm-btn--cancel" onClick={onClose} disabled={saving}>
                        Cancel
                    </button>
                    <button
                        className="gmm-btn gmm-btn--generate"
                        onClick={handleGenerate}
                        disabled={saving || blockers.length > 0 || onShelfCount === 0}
                    >
                        {saving ? "Generating..." : "Generate Menu"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default GenerateMenuModal;
